import { flows, quiz, responses } from '../services/api';
import type { Flow, FlowRun, ResponsePayload } from '../types';
import {
  FlowPrerequisiteBootstrapResult,
  QuestionFlowInitialBootstrap,
  QuizFlowInitialBootstrap,
} from 'components/QuestionFlow/types';
import { preloadPrimaryQuestionImage } from 'components/QuestionFlow/questionImageUtils';
import { resolveFlowPrerequisiteGuard } from 'components/QuestionFlow/useFlowPrerequisiteGuard';
import { preloadFlowTheme } from 'components/QuestionFlow/themes';

type FlowEntryBootstrapBase = {
  flow: Flow;
};

export type FlowEntryBootstrapResult =
  | {
      status: 'ready';
      kind: 'standard';
      bootstrap: QuestionFlowInitialBootstrap;
    }
  | {
      status: 'ready';
      kind: 'quiz';
      bootstrap: QuizFlowInitialBootstrap;
    }
  | {
      status: 'ready';
      kind: 'roleplay';
      bootstrap: {
        flow: Flow;
        flowRun: FlowRun;
        prerequisite: FlowPrerequisiteBootstrapResult;
      };
    }
  | (FlowEntryBootstrapBase & {
      status: 'blocked';
      blockingFlow: Flow;
    })
  | (FlowEntryBootstrapBase & {
      status: 'redirect_to_results';
    })
  | { status: 'course_locked'; courseId: number }
  | {
      status: 'access_denied';
      message: string | null;
    }
  | {
      status: 'error';
      message: string | null;
    };

interface BootstrapFlowEntryParams {
  flowName: string;
  courseId?: number | null;
  targetDate?: string | null;
  flowRunId?: number | null;
  bypassPrerequisiteCheck?: boolean;
  /** The route's `?mode=` value; voice entries skip the theme preload. */
  requestedMode?: string | null;
}

const getStartupErrorMessage = (error: unknown, fallback: string) => {
  const responseData = (error as { response?: { data?: { detail?: string; error?: string; message?: string } } })?.response?.data;
  return responseData?.detail ?? responseData?.error ?? responseData?.message ?? fallback;
};

const listInitialResponses = async (
  flow: Flow,
  flowRunId: number | null,
): Promise<ResponsePayload[]> => {
  const responseParams: Record<string, unknown> = {};

  if (typeof flowRunId === 'number') {
    responseParams.flow_run = flowRunId;
  } else if (typeof flow.id === 'number') {
    responseParams.flow = flow.id;
  }

  if (!Object.keys(responseParams).length) {
    return [];
  }

  try {
    const responseList = await responses.list(responseParams);
    return Array.isArray(responseList?.data) ? (responseList.data as ResponsePayload[]) : [];
  } catch (responseError) {
    console.error('Failed to load saved responses for flow startup', responseError);
    return [];
  }
};

const resolvePrerequisite = async (
  flowName: string,
  courseId: number | null,
  bypassPrerequisiteCheck: boolean,
): Promise<FlowPrerequisiteBootstrapResult> =>
  resolveFlowPrerequisiteGuard(flowName, {
    bypassPrerequisiteCheck,
    courseId,
  });

const bootstrapQuizEntry = async ({
  flow,
  flowName,
  courseId,
  bypassPrerequisiteCheck,
}: {
  flow: Flow;
  flowName: string;
  courseId: number | null;
  bypassPrerequisiteCheck: boolean;
}): Promise<FlowEntryBootstrapResult> => {
  const prerequisite = await resolvePrerequisite(flowName, courseId, bypassPrerequisiteCheck);
  if (prerequisite.lockedCourseId) {
    return { status: 'course_locked', courseId: prerequisite.lockedCourseId };
  }
  if (prerequisite.errorMessage) {
    return { status: 'error', message: prerequisite.errorMessage };
  }
  if (prerequisite.blockingFlow) {
    return { status: 'blocked', flow, blockingFlow: prerequisite.blockingFlow };
  }

  try {
    const introResponse = await quiz.getIntro(flow.id);
    const introPayload = introResponse?.data ?? null;
    const blockReason = introPayload?.block_reason ?? null;
    const hasReachedMaxAttempts = blockReason === 'max_attempts';
    const isRetakeBlocked = blockReason === 'retake_disabled_after_pass';
    const errorMessage = introPayload?.message ?? null;

    if (introPayload?.can_enter_quiz === false) {
      if (hasReachedMaxAttempts || isRetakeBlocked) {
        return { status: 'redirect_to_results', flow };
      }

      return {
        status: 'error',
        message: errorMessage ?? 'Unable to enter quiz.',
      };
    }

    // The quiz opens (intro first, then the themed questions): fetch its theme now.
    void preloadFlowTheme(flow.theme);

    return {
      status: 'ready',
      kind: 'quiz',
      bootstrap: {
        flow,
        intro: introPayload,
        prerequisite,
        hasReachedMaxAttempts,
        isRetakeBlocked,
        errorMessage,
      },
    };
  } catch (bootstrapError) {
    const accessData = (bootstrapError as { response?: { data?: { code?: string; course_id?: number } } })?.response?.data;
    if (accessData?.code === 'course_prerequisites_unmet' && accessData.course_id) {
      return { status: 'course_locked', courseId: accessData.course_id };
    }
    const httpStatus = (bootstrapError as { response?: { status?: number } })?.response?.status;
    if (httpStatus === 403) {
      return {
        status: 'access_denied',
        message: getStartupErrorMessage(bootstrapError, 'This quiz is unavailable.'),
      };
    }

    const blockReason = (bootstrapError as { response?: { data?: { block_reason?: string } } })?.response?.data?.block_reason;
    if (blockReason === 'max_attempts' || blockReason === 'retake_disabled_after_pass') {
      return { status: 'redirect_to_results', flow };
    }

    return {
      status: 'error',
      message: getStartupErrorMessage(bootstrapError, 'Unable to load quiz.'),
    };
  }
};

const bootstrapStandardFlowEntry = async ({
  flow,
  flowName,
  courseId,
  targetDate,
  flowRunId,
  bypassPrerequisiteCheck,
  requestedMode,
}: {
  flow: Flow;
  flowName: string;
  courseId: number | null;
  targetDate: string | null;
  flowRunId: number | null;
  bypassPrerequisiteCheck: boolean;
  requestedMode: string | null;
}): Promise<FlowEntryBootstrapResult> => {
  const prerequisite = await resolvePrerequisite(flowName, courseId, bypassPrerequisiteCheck);
  if (prerequisite.lockedCourseId) {
    return { status: 'course_locked', courseId: prerequisite.lockedCourseId };
  }
  if (prerequisite.errorMessage) {
    return { status: 'error', message: prerequisite.errorMessage };
  }
  if (prerequisite.blockingFlow) {
    return { status: 'blocked', flow, blockingFlow: prerequisite.blockingFlow };
  }

  try {
    const [questionsResponse, flowRunResponse] = await Promise.all([
      flows.getQuestions(flow.id, { filtered: true }),
      flows.getFlowRun(flowName, {
        targetDate: targetDate ?? undefined,
        flowRunId,
      }),
    ]);

    const questions = Array.isArray(questionsResponse?.data) ? questionsResponse.data : [];
    const flowRun = flowRunResponse?.data ?? null;
    const resolvedFlowRunId =
      typeof flowRun?.flow_run_id === 'number'
        ? flowRun.flow_run_id
        : typeof flowRun?.id === 'number'
          ? flowRun.id
          : flowRunId;
    const roleplayQuestions = questions.filter(
      (question) =>
        String(question?.question_type_display ?? question?.question_type ?? '')
          .trim()
          .toLowerCase() === 'roleplay',
    );
    // A flow whose only item is a roleplay keeps the dedicated full-page runtime.
    // Roleplay mixed with ordinary questions runs as a step inside QuestionFlow.
    if (roleplayQuestions.length === 1 && questions.length === 1) {
      if (!flowRun || typeof resolvedFlowRunId !== 'number') {
        return { status: 'error', message: 'Unable to start this roleplay practice.' };
      }
      return {
        status: 'ready',
        kind: 'roleplay',
        bootstrap: { flow, flowRun, prerequisite },
      };
    }
    // Standard and Voice share the effective module backdrop. Preloading here
    // overlaps its download with the canonical responses and image below.
    void preloadFlowTheme(flow.theme);
    const initialResponses = await listInitialResponses(flow, resolvedFlowRunId ?? null);
    await preloadPrimaryQuestionImage(questions[0] ?? null);

    return {
      status: 'ready',
      kind: 'standard',
      bootstrap: {
        flow,
        questions,
        flowRun,
        responses: initialResponses,
        prerequisite,
        initialImageReady: true,
      },
    };
  } catch (bootstrapError) {
    const accessData = (bootstrapError as { response?: { data?: { code?: string; course_id?: number } } })?.response?.data;
    if (accessData?.code === 'course_prerequisites_unmet' && accessData.course_id) {
      return { status: 'course_locked', courseId: accessData.course_id };
    }
    const httpStatus = (bootstrapError as { response?: { status?: number } })?.response?.status;
    if (httpStatus === 403) {
      return {
        status: 'access_denied',
        message: getStartupErrorMessage(bootstrapError, 'This module is unavailable.'),
      };
    }

    return {
      status: 'error',
      message: getStartupErrorMessage(bootstrapError, 'Unable to load module.'),
    };
  }
};

export const bootstrapFlowEntry = async ({
  flowName,
  courseId = null,
  targetDate = null,
  flowRunId = null,
  bypassPrerequisiteCheck = false,
  requestedMode = null,
}: BootstrapFlowEntryParams): Promise<FlowEntryBootstrapResult> => {
  try {
    // The course context makes `flow.theme` that course's theme when the flow
    // inherits, so the preload below fetches the scene learners will see.
    const flowResponse = await flows.getFlow(flowName, { courseId });
    const flow = flowResponse?.data ?? null;

    if (!flow) {
      return {
        status: 'error',
        message: 'Module not found.',
      };
    }

    if (flow.is_quiz) {
      return await bootstrapQuizEntry({
        flow,
        flowName,
        courseId,
        bypassPrerequisiteCheck,
      });
    }

    return await bootstrapStandardFlowEntry({
      flow,
      flowName,
      courseId,
      targetDate,
      flowRunId,
      bypassPrerequisiteCheck,
      requestedMode,
    });
  } catch (bootstrapError) {
    const accessData = (bootstrapError as { response?: { data?: { code?: string; course_id?: number } } })?.response?.data;
    if (accessData?.code === 'course_prerequisites_unmet' && accessData.course_id) {
      return { status: 'course_locked', courseId: accessData.course_id };
    }
    const httpStatus = (bootstrapError as { response?: { status?: number } })?.response?.status;
    if (httpStatus === 403) {
      return {
        status: 'access_denied',
        message: getStartupErrorMessage(bootstrapError, 'This module is unavailable.'),
      };
    }

    return {
      status: 'error',
      message: getStartupErrorMessage(bootstrapError, 'Unable to load module.'),
    };
  }
};
